import { Link, useParams } from '@tanstack/react-router';
import { useState } from 'react';
import {
  cn,
  Alert,
  backLinkClasses,
  Badge,
  Button,
  Card,
  DataList,
  DataRow,
  NotProvided,
  Page,
  PageHeader,
  Table,
  type Action,
  isNotFound,
} from '@evogard/ui';
import { DataCell } from '../DataCell.js';
import { formatDocument, formatPhone } from '@evogard/domain';
import { ThirdPartyForm } from './ThirdPartyForm.js';
import { VehicleForm } from './VehicleForm.js';
import {
  useThirdParty,
  useThirdPartyMutations,
  useThirdPartyPermissions,
} from './useThirdParties.js';
import { MissingRecord } from '../../../components/MissingRecord.js';

/**
 * A ficha de um terceiro: os dados da pessoa e TODOS os veículos dela, os
 * arquivados inclusive.
 *
 * **Arquivar não apaga** (convenção 9): o veículo pode estar num sinistro
 * aberto, e o sinistro continua apontando para ele. O que muda é que ele sai
 * da lista de onde se escolhe o carro de um sinistro novo. Por isso é
 * `neutro` e não `perigo` — reativar desfaz.
 *
 * O erro de arquivar mora na TELA: o painel de ações fecha antes de a ação
 * rodar, então não há onde mostrar a recusa dentro dele — mesmo desenho de
 * `DepartmentsScreen`.
 */
export function ThirdPartyScreen() {
  const { id } = useParams({ from: '/authenticated/claims/third-parties/$id' });
  const thirdPartyId = Number(id);
  const { canCreate, canEdit } = useThirdPartyPermissions();
  const query = useThirdParty(Number.isInteger(thirdPartyId) ? thirdPartyId : null);
  const { setVehicleArchived } = useThirdPartyMutations();
  const [editing, setEditing] = useState(false);
  const [adding, setAdding] = useState(false);

  const person = query.data;

  if (!Number.isInteger(thirdPartyId) || isNotFound(query.error)) {
    return (
      <MissingRecord
        title="Este terceiro não existe nesta base"
        list={{ to: '/claims/third-parties', label: 'Ver os terceiros' }}
      />
    );
  }

  return (
    <Page>
      <Link to="/claims/third-parties" className={backLinkClasses()}>
        Terceiros
      </Link>

      <PageHeader title={person?.name ?? (query.isLoading ? 'Carregando…' : 'Terceiro')}>
        A pessoa de fora da associação, e os veículos dela.
      </PageHeader>

      {query.isError && <Alert tone="erro">{query.error.message}</Alert>}
      {setVehicleArchived.error && (
        <Alert tone="erro">{setVehicleArchived.error.message}</Alert>
      )}

      {person && (
        <>
          {/* Corrigindo, o formulário TOMA o lugar do cartão: dentro dele, eram
              dois cartões e dois títulos para a mesma coisa. */}
          {editing ? (
            <ThirdPartyForm
              editing={person}
              onSaved={() => setEditing(false)}
              onCancel={() => setEditing(false)}
            />
          ) : (
            <Card
              title="Dados"
              actions={
                canEdit && (
                  <Button variant="contorno" size="sm" onClick={() => setEditing(true)}>
                    Corrigir
                  </Button>
                )
              }
            >
              <DataList>
                <DataRow label="Nome">{person.name}</DataRow>
                <DataRow label="Telefone">
                  <span className="tabular-nums">{formatPhone(person.phone)}</span>
                </DataRow>
                <DataRow label="E-mail">{person.email ?? <NotProvided />}</DataRow>
                <DataRow label="CPF ou CNPJ">
                  {person.document ? (
                    <span className="tabular-nums">
                      {formatDocument(person.document)}
                    </span>
                  ) : (
                    <NotProvided />
                  )}
                </DataRow>
              </DataList>
            </Card>
          )}

          {/* O veículo novo abre ACIMA da tabela, e ela continua à vista: é nela
              que se confere se a placa já está com esta pessoa. */}
          {adding && (
            <VehicleForm
              thirdPartyId={person.id}
              ownerName={person.name}
              onSaved={() => setAdding(false)}
              onCancel={() => setAdding(false)}
            />
          )}

          <Card
            title="Veículos"
            actions={
              canCreate &&
              !adding && (
                <Button
                  variant="contorno"
                  size="sm"
                  icon="mais"
                  onClick={() => setAdding(true)}
                >
                  Vincular veículo
                </Button>
              )
            }
            bleed
          >
            <Table
              label={`Veículos de ${person.name}`}
              items={person.vehicles}
              loading={false}
              rowKey={(v) => String(v.id)}
              empty={
                `${person.name} ainda não tem veículo cadastrado. O aviso do terceiro precisa ` +
                'do carro dele — vincule aqui ou pelo próprio aviso.'
              }
              rowTitle={(v) => v.plate}
              rowSubtitle={(v) => `${v.model} · ${v.manufactureYear}/${v.modelYear}`}
              rowIcon="veiculo"
              rowActions={(v): Action[] => {
                if (!canEdit) return [];
                const archived = Boolean(v.archivedAt);
                return [
                  {
                    label: archived ? 'Reativar' : 'Arquivar',
                    icon: archived ? 'atualizar' : 'bloqueado',
                    run: () =>
                      setVehicleArchived.mutate({
                        thirdPartyId: person.id,
                        vehicleId: v.id,
                        archived: !archived,
                      }),
                  },
                ];
              }}
              columns={[
                {
                  title: 'Placa',
                  minWidth: '10rem',
                  cell: (v) => (
                    <DataCell icon="veiculo">
                      <span
                        className={cn(
                          'font-mono tabular-nums',
                          v.archivedAt && 'text-texto-suave',
                        )}
                      >
                        {v.plate}
                      </span>
                      {v.vehicleId !== null && (
                        <Badge tone="informacao">Carro de associado</Badge>
                      )}
                      {v.archivedAt && <Badge tone="neutro">Arquivado</Badge>}
                    </DataCell>
                  ),
                },
                {
                  title: 'Modelo',
                  alignment: 'inicio' as const,
                  minWidth: '12rem',
                  cell: (v) => <DataCell icon="etiqueta">{v.model}</DataCell>,
                },
                {
                  title: 'Ano',
                  minWidth: '9rem',
                  cell: (v) => (
                    <DataCell icon="calendario" hint="Ano de fabricação / ano do modelo">
                      <span className="tabular-nums">
                        {v.manufactureYear}/{v.modelYear}
                      </span>
                    </DataCell>
                  ),
                },
              ]}
            />
          </Card>
        </>
      )}
    </Page>
  );
}
